import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Modern kit — a church-style wall segment with a tall arched window: a
// rectangular glass opening whose head is a semicircular arch. The arch is a
// half-cylinder of glass (axis along the wall thickness) set in a stone
// frame; the glass is slightly thicker than the wall so the pane reads on
// both faces. Sill at 0.6 m, arch top near 2.2 m; stone-grey wall tone.
const STONE = "#b8b2a8";
const STONE_DARK = "#9a948a";
const GLASS = "#9fc4d4";

export const wallArchedWindow = defineModel({
  id: "wall-arched-window",
  batchId: "modern",
  name: "Wall with arched window",
  tags: ["modern", "interior", "outdoor", "structure", "wall", "window", "arch", "church"],
  summary: "A stone-grey wall segment with a tall arched window, sill at 0.6 m, arch top at 2.2 m.",
  description:
    "A one-tile stone-toned wall of the Modern Living kit with a tall " +
    "church-style arched window: a rectangular glass panel from the 0.6 m " +
    "sill up to 1.9 m, headed by a semicircular glass arch (radius 0.3 m, " +
    "top at 2.2 m) in a stone frame. Same 0.12 m thickness and 2.6 m height " +
    "as wall-plain.",
  tiles: { w: 1, h: 1 },
  footprint: 1.4,
  parts(): Part[] {
    return [
      // full-height piers either side of the 0.6 m opening
      { geometry: new THREE.BoxGeometry(0.2, 2.6, 0.12), color: STONE, position: [-0.4, 1.3, 0] },
      { geometry: new THREE.BoxGeometry(0.2, 2.6, 0.12), color: STONE, position: [0.4, 1.3, 0] },
      // wall below the sill (0 -> 0.6)
      { geometry: new THREE.BoxGeometry(0.6, 0.6, 0.12), color: STONE, position: [0, 0.3, 0] },
      // wall above the arch spring line (1.9 -> 2.6); the arch overlays it
      { geometry: new THREE.BoxGeometry(0.6, 0.7, 0.12), color: STONE, position: [0, 2.25, 0] },
      // stone frame around the rectangular part, slightly proud of the wall
      { geometry: new THREE.BoxGeometry(0.68, 1.3, 0.13), color: STONE_DARK, position: [0, 1.25, 0] },
      // stone arch frame — a half-cylinder ring, proud of the wall face
      {
        geometry: new THREE.CylinderGeometry(0.34, 0.34, 0.13, 16, 1, false, Math.PI / 2, Math.PI),
        color: STONE_DARK,
        position: [0, 1.9, 0],
        rotation: [Math.PI / 2, 0, 0],
      },
      // rectangular glass panel (0.6 -> 1.9), thicker than the frame
      { geometry: new THREE.BoxGeometry(0.56, 1.3, 0.15), color: GLASS, position: [0, 1.25, 0] },
      // semicircular glass arch head (spring at 1.9, top near 2.2)
      {
        geometry: new THREE.CylinderGeometry(0.28, 0.28, 0.15, 16, 1, false, Math.PI / 2, Math.PI),
        color: GLASS,
        position: [0, 1.9, 0],
        rotation: [Math.PI / 2, 0, 0],
      },
      // projecting stone sill ledge
      { geometry: new THREE.BoxGeometry(0.8, 0.06, 0.2), color: STONE_DARK, position: [0, 0.6, 0] },
    ];
  },
  animations: [],
});
